import { Box, Button, FormField, Header, Input, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import { FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import SingleEmployeeLookup, { SelectedEmployee } from 'src/components/generic-components/employee-lookup/SingleEmployeeLookup';
import MultiEmployeeLookup from 'src/components/generic-components/employee-lookup/MultiEmployeeLookup';
import { ContactDetails, ContactInformation, HomePageWizard } from '../HomepageModel';
import './HomePageContactsStep.scss';

interface HomePageContactsStepProps {
  formik: FormikProps<HomePageWizard>;
}

// ── Helper: convert ContactDetails[] ↔ SelectedEmployee[] ──

const toSelectedEmployees = (contacts: ContactDetails[]): SelectedEmployee[] =>
  contacts.map((c) => ({ login: c.contactAlias, fullName: c.contactName }));

const toContactDetail = (emp: SelectedEmployee): ContactDetails => ({
  contactAlias: emp.login,
  contactName: emp.fullName
});

// ── Per-contact validation (exported so wizard can use it) ──

export interface ContactValidationErrors {
  groupName?: string;
  primaryContact?: string;
}

export const validateContacts = (contacts: ContactInformation[]): Map<string, ContactValidationErrors> => {
  const errors = new Map<string, ContactValidationErrors>();

  for (const c of contacts) {
    const fieldErrors: ContactValidationErrors = {};

    if (!c.groupName || c.groupName.trim().length === 0) {
      fieldErrors.groupName = 'Group name is required';
    }

    if (!c.primaryContact?.contactAlias || c.primaryContact.contactAlias.trim().length === 0) {
      fieldErrors.primaryContact = 'Primary contact is required';
    }

    if (Object.keys(fieldErrors).length > 0) {
      errors.set(c.contactId, fieldErrors);
    }
  }

  return errors;
};

// ── Main component ──

const HomePageContactsStep: React.FC<HomePageContactsStepProps> = ({ formik }) => {
  const contacts = formik.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
  const [selectedContacts, setSelectedContacts] = useState<ContactInformation[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  // Use a ref to always read the freshest contacts from formik
  const formikRef = useRef(formik);
  formikRef.current = formik;

  const setContacts = useCallback((updated: ContactInformation[]) => {
    formikRef.current.setFieldValue('homePageDetailsAndWidgets.contactsWidgetContacts', updated);
  }, []);

  /** Always reads fresh contacts from formik to avoid stale closures */
  const handleUpdateContactField = useCallback((contactId: string, field: string, value: any) => {
    const currentContacts = formikRef.current.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
    const updated = currentContacts.map((c) => (c.contactId === contactId ? { ...c, [field]: value } : c));
    formikRef.current.setFieldValue('homePageDetailsAndWidgets.contactsWidgetContacts', updated);
  }, []);

  /** submitEdit handler for editConfig columns (Group Name, SIM Links, Slack Channels) */
  const handleSubmitEdit: TableProps.SubmitEditFunction<ContactInformation> = useCallback(
    (item, column, newValue) => {
      const columnId = column.id;
      if (!columnId) return;

      if (columnId === 'groupName') {
        handleUpdateContactField(item.contactId, 'groupName', newValue as string);
      } else if (columnId === 'simLinks') {
        const names = (newValue as string)
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean);
        const existingLinks = item.simLinks ?? [];
        handleUpdateContactField(
          item.contactId,
          'simLinks',
          names.map((name, index) => ({
            name,
            // Preserve URL by index position: a rename at position N keeps its URL
            url: existingLinks[index]?.url ?? ''
          }))
        );
      } else if (columnId === 'slackChannels') {
        const names = (newValue as string)
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean);
        const existingChannels = item.slackChannels ?? [];
        handleUpdateContactField(
          item.contactId,
          'slackChannels',
          names.map((name, index) => ({
            name,
            // Preserve URL by index position: a rename at position N keeps its URL
            url: existingChannels[index]?.url ?? ''
          }))
        );
      }
    },
    [handleUpdateContactField]
  );

  const handleAddBlankContact = useCallback(() => {
    const currentContacts = formikRef.current.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
    const blank: ContactInformation = {
      contactId: uuidv4(),
      groupName: '',
      primaryContact: { contactAlias: '', contactName: '' },
      otherContacts: [],
      simLinks: [],
      slackChannels: []
    };
    setContacts([blank, ...currentContacts]);
    setSelectedContacts([blank]);
    setShowValidation(true);
  }, [setContacts]);

  const handleDeleteContacts = useCallback(() => {
    const currentContacts = formikRef.current.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
    const selectedIds = new Set(selectedContacts.map((c) => c.contactId));
    setContacts(currentContacts.filter((c) => !selectedIds.has(c.contactId)));
    setSelectedContacts([]);
  }, [selectedContacts, setContacts]);

  const handleMoveUp = useCallback(() => {
    if (selectedContacts.length !== 1) return;
    const currentContacts = formikRef.current.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
    const idx = currentContacts.findIndex((c) => c.contactId === selectedContacts[0].contactId);
    if (idx <= 0) return;
    const updated = [...currentContacts];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setContacts(updated);
  }, [selectedContacts, setContacts]);

  const handleMoveDown = useCallback(() => {
    if (selectedContacts.length !== 1) return;
    const currentContacts = formikRef.current.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
    const idx = currentContacts.findIndex((c) => c.contactId === selectedContacts[0].contactId);
    if (idx < 0 || idx >= currentContacts.length - 1) return;
    const updated = [...currentContacts];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setContacts(updated);
  }, [selectedContacts, setContacts]);

  // Expose validation for the wizard
  const validationErrors = useMemo(() => validateContacts(contacts), [contacts]);
  const triggerValidation = useCallback(() => {
    setShowValidation(true);
    return validationErrors.size === 0;
  }, [validationErrors]);

  useEffect(() => {
    (formik as any).__contactsValidation = { triggerValidation, hasErrors: validationErrors.size > 0 };
  }, [formik, triggerValidation, validationErrors]);

  const getFieldError = useCallback(
    (contactId: string, field: keyof ContactValidationErrors): string | undefined => {
      if (!showValidation) return undefined;
      return validationErrors.get(contactId)?.[field];
    },
    [showValidation, validationErrors]
  );

  const columnDefinitions: TableProps.ColumnDefinition<ContactInformation>[] = useMemo(
    () => [
      {
        id: 'groupName',
        header: 'Group Name',
        width: 200,
        cell: (item: ContactInformation) => item.groupName || '-',
        editConfig: {
          ariaLabel: 'Group Name',
          editIconAriaLabel: 'editable',
          errorIconAriaLabel: 'Group Name Error',
          editingCell: (item, { currentValue, setValue }) => (
            <Input
              autoFocus={true}
              value={currentValue ?? item.groupName}
              onChange={(event) => setValue(event.detail.value)}
              placeholder="e.g., DaS FinTech Lead"
            />
          ),
          validation: (item, value) => {
            const val = value ?? item.groupName;
            if (!val || val.trim().length === 0) {
              return 'Group name is required';
            }
            return undefined;
          }
        }
      },
      {
        id: 'primaryContact',
        header: 'Primary Contact',
        width: 280,
        cell: (item: ContactInformation) => {
          const value: SelectedEmployee | undefined = item.primaryContact?.contactAlias
            ? { login: item.primaryContact.contactAlias, fullName: item.primaryContact.contactName }
            : undefined;

          return (
            <div className="contacts-lookup-cell">
              <FormField errorText={getFieldError(item.contactId, 'primaryContact')}>
                <SingleEmployeeLookup
                  label=""
                  placeholder="Search by name"
                  value={value}
                  onChange={(emp) => {
                    handleUpdateContactField(item.contactId, 'primaryContact', emp ? toContactDetail(emp) : { contactAlias: '', contactName: '' });
                  }}
                />
              </FormField>
            </div>
          );
        }
      },
      {
        id: 'otherContacts',
        header: 'Other Contacts',
        width: 320,
        cell: (item: ContactInformation) => {
          const value = toSelectedEmployees(item.otherContacts ?? []);

          return (
            <div className="contacts-lookup-cell">
              <MultiEmployeeLookup
                label=""
                placeholder="Search by name"
                value={value}
                onChange={(emps) => {
                  handleUpdateContactField(
                    item.contactId,
                    'otherContacts',
                    emps.map((e) => toContactDetail(e))
                  );
                }}
              />
            </div>
          );
        }
      },
      {
        id: 'simLinks',
        header: 'SIM Links',
        width: 200,
        cell: (item: ContactInformation) => <div className="contacts-sim-cell">{(item.simLinks ?? []).map((s) => s.name).join(', ') || '-'}</div>,
        editConfig: {
          ariaLabel: 'SIM Links',
          editIconAriaLabel: 'editable',
          errorIconAriaLabel: 'SIM Links Error',
          editingCell: (item, { currentValue, setValue }) => (
            <Input
              autoFocus={true}
              value={currentValue ?? (item.simLinks ?? []).map((s) => s.name).join(', ')}
              onChange={(event) => setValue(event.detail.value)}
              placeholder="e.g., Feature Request, Bug Report"
            />
          )
        }
      },
      {
        id: 'slackChannels',
        header: 'Slack Channels',
        width: 200,
        cell: (item: ContactInformation) => (
          <div className="contacts-slack-cell">{(item.slackChannels ?? []).map((ch) => ch.name).join(', ') || '-'}</div>
        ),
        editConfig: {
          ariaLabel: 'Slack Channels',
          editIconAriaLabel: 'editable',
          errorIconAriaLabel: 'Slack Channels Error',
          editingCell: (item, { currentValue, setValue }) => (
            <Input
              autoFocus={true}
              value={currentValue ?? (item.slackChannels ?? []).map((ch) => ch.name).join(', ')}
              onChange={(event) => setValue(event.detail.value)}
              placeholder="e.g., #das-verity-users"
            />
          )
        }
      }
    ],
    [handleUpdateContactField, getFieldError]
  );

  return (
    <div className="contacts-table-wrapper">
      <Table
        columnDefinitions={columnDefinitions}
        items={contacts}
        submitEdit={handleSubmitEdit}
        selectionType="multi"
        selectedItems={selectedContacts}
        onSelectionChange={({ detail }) => setSelectedContacts(detail.selectedItems)}
        trackBy="contactId"
        ariaLabels={{
          activateEditLabel: (column, item) => `Edit ${item.groupName} ${column.header}`,
          cancelEditLabel: (column) => `Cancel editing ${column.header}`,
          submitEditLabel: (column) => `Submit editing ${column.header}`,
          tableLabel: 'Key Contacts table with inline editing'
        }}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No contacts</b>
            <Box variant="p" color="inherit">
              Click &quot;Add contact&quot; to create a new key contact entry.
            </Box>
          </Box>
        }
        header={
          <Header
            variant="h2"
            counter={`(${contacts.length})`}
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                <Button iconName="add-plus" onClick={handleAddBlankContact}>
                  Add contact
                </Button>
                <Button onClick={handleDeleteContacts} disabled={selectedContacts.length === 0}>
                  Delete
                </Button>
                <Button onClick={handleMoveUp} disabled={selectedContacts.length !== 1} iconName="angle-up">
                  Move up
                </Button>
                <Button onClick={handleMoveDown} disabled={selectedContacts.length !== 1} iconName="angle-down">
                  Move down
                </Button>
              </SpaceBetween>
            }
          >
            Key Contacts
          </Header>
        }
      />
    </div>
  );
};

export default HomePageContactsStep;
